/**
 * Время в интерфейсе — всегда московское.
 *
 * Сервер хранит и отдаёт время в UTC «голой» строкой без таймзоны
 * («2026-09-01T18:57:40»). JS такую строку считает ЛОКАЛЬНЫМ временем браузера,
 * поэтому история размещения показывала 18:57 вместо московских 21:57.
 * Здесь одно место, где серверная строка превращается в дату: дописываем Z,
 * если таймзоны нет, и печатаем всё в Europe/Moscow — независимо от того, где
 * стоит компьютер (склад, дом, телефон в поездке).
 */
const MSK = 'Europe/Moscow';

/** Серверную строку → Date. Без таймзоны считаем UTC. */
export function parseServer(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const s = String(value);
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
  const d = new Date(hasTz ? s : `${s.replace(' ', 'T')}Z`);
  return isNaN(d.getTime()) ? null : d;
}

const fmt = (value, opts) => {
  const d = parseServer(value);
  return d ? new Intl.DateTimeFormat('ru-RU', { timeZone: MSK, ...opts }).format(d) : '';
};

/** 21:57 */
export const fmtTime = (v) => fmt(v, { hour: '2-digit', minute: '2-digit' });
/** 01.09.2026 */
export const fmtDate = (v) => fmt(v, { day: '2-digit', month: '2-digit', year: 'numeric' });
/** 01.09.2026 21:57 (Intl ставит запятую «01.09.2026, 21:57» — убираем, как в описании) */
export const fmtDateTime = (v) => fmt(v, {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}).replace(', ', ' ');
/** 1 сентября */
export const fmtDayMonth = (v) => fmt(v, { day: 'numeric', month: 'long' });

/** YYYY-MM-DD этой даты по Москве — для группировки по дням. */
export function mskDayKey(value) {
  // «2026-09-01» без времени — календарный день, сдвигать нельзя.
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = parseServer(value);
  if (!d) return '';
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: MSK, year: 'numeric', month: '2-digit', day: '2-digit',
  });
  return p.format(d);
}

/** «Сегодня» / «Вчера» / 01.09.2026 — для заголовков групп. */
export function fmtDayLabel(dayKey) {
  if (!dayKey) return '';
  const today = mskDayKey(new Date());
  const yest = mskDayKey(new Date(Date.now() - 86400000));
  if (dayKey === today) return 'Сегодня';
  if (dayKey === yest) return 'Вчера';
  const [Y, M, D] = String(dayKey).split('-');
  return `${D}.${M}.${Y}`;
}

/** YYYY-MM-DD сегодня по Москве — «сегодня» и дефолты периодов фильтров. */
export const mskToday = () => mskDayKey(new Date());

/**
 * Сдвиг ключа дня YYYY-MM-DD на n дней (вчера = -1, неделя назад = -7).
 * Считаем от UTC-полуночи ключа — локальная таймзона браузера не участвует.
 */
export function mskAddDays(key, n) {
  if (!key) return '';
  const [Y, M, D] = String(key).slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(Y, M - 1, D + (n || 0))).toISOString().slice(0, 10);
}

/** Понедельник недели ключа дня YYYY-MM-DD (тоже YYYY-MM-DD). */
export function mskWeekStart(key) {
  if (!key) return '';
  const [Y, M, D] = String(key).slice(0, 10).split('-').map(Number);
  const dow = (new Date(Date.UTC(Y, M - 1, D)).getUTCDay() + 6) % 7; // пн=0
  return mskAddDays(key, -dow);
}

/** Час (0–23) сейчас по Москве — для «Доброе утро/день/вечер». */
export function mskHour(value = new Date()) {
  const d = parseServer(value);
  if (!d) return 0;
  const h = new Intl.DateTimeFormat('en-GB', { timeZone: MSK, hour: '2-digit', hourCycle: 'h23' }).format(d);
  return Number(h) % 24;
}

/** 01.09 21:57 — короткая дата+время без года (по Москве). */
export function fmtShortDateTime(value) {
  const d = parseServer(value);
  if (!d) return '';
  const parts = {};
  new Intl.DateTimeFormat('ru-RU', {
    timeZone: MSK, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d).forEach((p) => { parts[p.type] = p.value; });
  return `${parts.day}.${parts.month} ${parts.hour}:${parts.minute}`;
}

/** Серверное время (UTC) → значение <input type="datetime-local"> по Москве: 2026-09-01T21:57. */
export function toMskInput(value) {
  const d = parseServer(value);
  if (!d) return '';
  // Москва — фиксированные UTC+3 (без перехода на летнее время)
  return new Date(d.getTime() + 3 * 3600 * 1000).toISOString().slice(0, 16);
}

/** Значение datetime-local, введённое по Москве → ISO UTC с «Z» для сервера. */
export function fromMskInput(str) {
  if (!str) return null;
  const s = String(str).length === 16 ? `${str}:00` : String(str);
  const t = Date.parse(`${s}+03:00`);
  return isNaN(t) ? null : new Date(t).toISOString();
}
